import { fromNet, toCents, toNet, toNumber } from "../../shared/money";
import { itemTax, partsPriceOf, quoteRate } from "./data";
import type { ItemPricing, MaterialRow, PriceListData, PricingRule, TaxQuote } from "./meta";
import { asGroupTaxed, calcPrice, costWithLabour, estimatedPartCost, itemProfit, needsCheck, partItem, pricedAs, type Profit, samePrice } from "./pricing";

/*
 * An item's pricing draft (its Pricing tab's, kept by its window) and what follows from it: the item as it has it, the price it
 * gives, and what the item makes now and once updated (ProfitBox, on its Pricing and Costing tabs). Apart from the tab, so the box
 * can use them too.
 */

/** An item's own pricing being edited, not saved yet (kept in its window, like a group's draft). */
export interface ItemDraft {
    /** Its own rule, or null: a typed price. */
    rule: PricingRule | null;
    /** The typed price, as typed (in `tax`'s terms): for a typed price. */
    price: string;
    /** How it's taxed (its rate resolved: the account default when it has none). */
    tax: TaxQuote;
    /** In a group, starting to override its price (its Override pressed): saving makes it its own (an override), Cancel doesn't. */
    override?: boolean;
}

/** The item as a draft has it: taxed the draft's way (its part's cost the same before tax), with its typed price. */
export function itemAsDrafted(data: PriceListData, item: MaterialRow, d: ItemDraft, partCost?: string) {
    const { tax } = quoteRate(data, d.tax);
    // Its part's cost (in its basis): as it is, or (`partCost`) as it'll be once its cost is updated.
    const part = partCost === undefined ? partItem(data, item) : { ...item, cost: partCost };
    const taxed = asGroupTaxed(part, itemTax(data, item), { ...d.tax, tax });
    return { item: { ...taxed, price: d.rule ? item.price : d.price }, tax };
}

/**
 * The price a draft gives (in its tax's terms): its rule's (a kit's from its parts' prices, as they are), or its typed price. Worked
 * out from its part's cost as it is, or (`partCost`) as it'll be once its cost is updated.
 */
export function draftPrice(data: PriceListData, item: MaterialRow, d: ItemDraft, partCost?: string) {
    if (!d.rule) return d.price;
    const now = itemAsDrafted(data, item, d, partCost);
    return calcPrice(now.item, now.tax, { ...d.rule, partsPrice: partsPriceOf(data, item.uuid) }, data.settings)?.price ?? toCents(toNumber(item.price));
}

/** What an item makes in one case (now, or once updated): its price before tax (at `tax`), its labour time, and its Profit. */
export interface ProfitCase {
    priceNet: number;
    tax: number;
    minutes: number;
    profit: Profit;
}

/**
 * What the item is estimated to make, before tax, now (ServiceM8's price and cost as they are) and once updated (its new price,
 * taxed as it'll be, on the cost it'll have: pricing.ts itemProfit). Once updated, its price is the one its pricing gives from its
 * part's cost as it'll be (a cost that moves can move its price: Cost + % and Cost + amount), as updating both at once does
 * (supplierPrices.ts updatePriceAndCost). Being edited: its pricing draft's price and labour, and its cost draft's cost
 * (`costTarget`: its cost as that would make it). `changes`: once updated isn't now (two to show).
 */
export function profitCases(data: PriceListData, item: MaterialRow, draft: ItemDraft | null, costTarget?: string): { now: ProfitCase; next: ProfitCase; changes: boolean } {
    const itemRate = itemTax(data, item);
    const { tax } = itemRate;
    const pricing = data.itemPricing[item.uuid];
    // Its cost being changed (its Costing tab): estimated on the cost that gives.
    if (costTarget !== undefined) {
        const { [item.uuid]: _was, ...others } = data.costUpdates;
        data = { ...data, costUpdates: samePrice(costTarget, item.cost) ? others : { ...others, [item.uuid]: costTarget } };
    }
    // As ServiceM8 has it, its labour time is the one its figures were set with (recorded with the labour in its cost), else its
    // pricing's.
    // (Not a number: not recorded, e.g. data from before it was: its pricing's, so nothing's worked out from nothing.)
    const recorded = data.labourInMinutes?.[item.uuid];
    const minutesNow = typeof recorded === "number" && Number.isFinite(recorded) ? recorded : (pricing?.labourMinutes ?? 0);
    const now: ProfitCase = { profit: itemProfit({ ...data, costUpdates: {} }, item, tax), priceNet: toNet(toNumber(item.price), item.price_includes_taxes, tax), tax, minutes: minutesNow };
    // Once updated: its labour time as its pricing has it.
    let next: ProfitCase = { ...now, profit: itemProfit(data, item, tax), minutes: pricing?.labourMinutes ?? 0 };
    let changes = item.uuid in data.costUpdates;
    if (draft) {
        // Being edited: at the price the draft gives (taxed its way), on its cost with the draft's labour (the part's as estimated).
        const minutes = draft.rule?.labourMinutes ?? 0;
        const withLabour = { ...data, itemPricing: { ...data.itemPricing, [item.uuid]: { ...pricing, labourMinutes: minutes } as ItemPricing } };
        const part = toCents(fromNet(itemProfit(data, item, tax).partNet, item.price_includes_taxes, tax));
        const target = costWithLabour(withLabour, item, part);
        const { [item.uuid]: _was, ...others } = data.costUpdates;
        const drafted = { ...withLabour, costUpdates: samePrice(target, item.cost) ? others : { ...others, [item.uuid]: target } };
        const after = itemAsDrafted(data, item, draft, part);
        const price = draftPrice(data, item, draft, part);
        next = { profit: itemProfit(drafted, { ...after.item, price }, after.tax, { was: item }), priceNet: toNet(toNumber(price), after.item.price_includes_taxes, after.tax), tax: after.tax, minutes };
        changes = !samePrice(price, item.price) || item.uuid in drafted.costUpdates || after.item.price_includes_taxes !== item.price_includes_taxes;
    } else {
        // From its part's cost as it'll be (its cost to update's, or its cost draft's).
        const check = needsCheck({ ...item, cost: estimatedPartCost(data, item) }, itemRate, pricing, data.settings);
        changes ||= check !== null;
        if (check?.taxTo) {
            const after = pricedAs(item, itemRate, pricing);
            next = { ...next, profit: itemProfit(data, { ...after.item, price: check.target }, after.tax, { was: item }), priceNet: toNet(toNumber(check.target), after.item.price_includes_taxes, after.tax), tax: after.tax };
        } else if (check) {
            next = { ...next, profit: itemProfit(data, { ...item, price: check.target }, tax), priceNet: toNet(toNumber(check.target), item.price_includes_taxes, tax) };
        }
    }
    return { now, next, changes };
}
